import { useEffect, useState } from 'react';
import { PrimaryButton, SecondaryButton } from './FigmaComponents';
import { api } from './api';
import type { LocationGrant, Profile } from './types';

type Recipient = { user: { id: string; name: string }; role: string };

export function AccessGrantsPanel({ familyId, membership, onChanged }: {
  familyId: string;
  membership: Profile['memberships'][number];
  onChanged?: () => void;
}) {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [authorized, setAuthorized] = useState<Record<string, LocationGrant>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  async function load() {
    const result = await api<{ grants: LocationGrant[]; recipients: Recipient[] }>(`/families/${familyId}/grants`);
    setRecipients(result.recipients);
    setAuthorized(Object.fromEntries(result.grants.filter(grant => grant.authorized).map(grant => [grant.recipientUserId, grant])));
  }

  useEffect(() => { void load().catch(error => setMessage(error instanceof Error ? error.message : 'Unable to load who you have authorized')); }, [familyId]);

  async function setAuthorization(recipient: Recipient, next: boolean) {
    setBusyId(recipient.user.id); setMessage('');
    try {
      if (next) await api(`/families/${familyId}/grants/${recipient.user.id}`, { method: 'PUT', body: JSON.stringify({ authorized: true }) });
      else await api(`/families/${familyId}/grants/${recipient.user.id}`, { method: 'DELETE' });
      await load();
      onChanged?.();
      setMessage(next ? `${recipient.user.name} can now see your location.` : `${recipient.user.name} can no longer see your location.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to update authorization');
    } finally {
      setBusyId(null);
    }
  }

  return <section className="settings-card access-grants-card">
    <small>WHO CAN SEE YOU</small>
    <h2>Authorize people one by one</h2>
    <p>Anyone you authorize can see your live location, your history, and can use Find Now and Ping. Anyone you do not authorize sees nothing at all — being in the same family is not enough.</p>
    {!membership.shareLocation && <div className="safety-notice">Sharing is paused, so nobody can see you right now — including the people listed below. Turn sharing back on to restore their access.</div>}
    {message && <div className="grant-message">{message}</div>}
    <div className="grant-list">
      {recipients.map(recipient => {
        const grant = authorized[recipient.user.id];
        const busy = busyId === recipient.user.id;
        return <article className="grant-card" key={recipient.user.id}>
          <div className="grant-heading">
            <div>
              <strong>{recipient.user.name}</strong>
              <span>{grant ? `Authorized ${new Date(grant.startsAt).toLocaleDateString()}` : 'Not authorized'}</span>
            </div>
            <span className={`fig-status ${grant ? 'live' : 'offline'}`}>
              <span className="fig-status-dot" />{grant ? 'CAN SEE YOU' : 'CANNOT SEE YOU'}
            </span>
          </div>
          <div className="grant-actions">
            {grant
              ? <SecondaryButton disabled={busy} onClick={() => setAuthorization(recipient, false)}>Remove access</SecondaryButton>
              : <PrimaryButton disabled={busy} onClick={() => setAuthorization(recipient, true)}>Authorize {recipient.user.name.split(' ')[0]}</PrimaryButton>}
          </div>
        </article>;
      })}
      {!recipients.length && <div className="empty-state"><strong>No other family members yet</strong><span>Invite someone before authorizing them.</span></div>}
    </div>
  </section>;
}
